import { ModelUpdatedEvent } from "ag-grid-community"
import { useCallback, useEffect, useMemo, useState } from "react"

import {
  columnsDefValueFormatterMapping,
  getCellStylePipeline,
} from "../../../components/agGridMapping"
import { IGridProps } from "../../../components/Grid"
import { useAgGrid } from "../../../components/hooks/useAgGrid"

/**
 * Custom hook to handle ag grid tree data from scient backend
 */
export const useAgGridTreeData = ({ columns, rows, contextMenuItemsDef, masks }: IGridProps) => {
  const {
    gridRef,
    sizeColumnsToFit,
    gridOptions: { defaultColDef, getContextMenuItems },
  } = useAgGrid({
    columns,
    contextMenuItemsDef,
    masks,
  })

  /**
   * Handles internal state of expanded nodes
   * to handle refresh of expanded nodes when data is updated
   */
  const [expandedParents, setExpandedParents] = useState<string[]>([])

  /**
   * Memoized columns
   * "Account", "Book" are not included because they are grouped into path in order to build the treeData.
   */
  const columnsData = useMemo(() => {
    return columns
      ?.filter(col => !["account", "book"].includes(col.field))
      .map(column => {
        return {
          field: column.field,
          headerName: column.headerName,
          filter: column.filter,
          valueFormatter: columnsDefValueFormatterMapping[column.valueFormatter],
          cellStyle: getCellStylePipeline(column.cellStyles, masks),
          hide: column.hide,
          menuTabs: column.filter ? ["filterMenuTab"] : [],
        }
      })
  }, [columns])

  /**
   * Handles events when a node is expanded in ag grid internal state
   * to match local component state
   */
  const onModelUpdated = useCallback(
    (event: ModelUpdatedEvent) => {
      event.api.forEachNode(node => {
        if (node.expanded) {
          setExpandedParents(expandedParents => {
            return !expandedParents.includes(node.data["account"])
              ? [...expandedParents, node.data["account"]]
              : expandedParents
          })
        } else {
          if (expandedParents.includes(node.data["account"]) && node.level === 0) {
            setExpandedParents(expandedParents => {
              return [...expandedParents.filter(name => name !== node.data["account"])]
            })
          }
        }
      })
    },
    [expandedParents],
  )

  /**
   * When rowsData is updated, we need to expand nodes that were expanded before
   */
  useEffect(() => {
    if (rows) {
      gridRef.current?.api?.forEachNode(node => {
        if (node.level === 0 && expandedParents.includes(node.data["account"])) {
          node.expanded = true
        }
      })
      gridRef.current?.api?.onGroupExpandedOrCollapsed()
    }
  }, [expandedParents, gridRef, rows])

  return {
    sizeColumnsToFit,
    columnsData,
    defaultColDef,
    gridRef,
    onModelUpdated,
    getContextMenuItems,
  }
}
